'use client';

/**
 * Admin economics readout (W7 finance layer, observability-plan.md).
 *
 * Reads GET /api/admin/economics: the finance_daily window, the liability
 * balances, the creator-pool position, and the realized-vs-model tiles.
 * Everything on this page is a readout; nothing here moves money.
 *
 * Access is enforced by the API route (admin only); this page just renders
 * the 403 as a friendly message.
 */

import { useCallback, useEffect, useMemo, useState } from 'react';
import { Activity, Banknote, Coins, Gauge, Landmark, Percent, RefreshCw, Scale, ShieldAlert } from 'lucide-react';

const NANOCREDITS_PER_USD = 1e9;

interface DailyRow {
  day: string;
  cashInGross: number;
  stripeFees: number;
  creditsMintedPlan: number;
  creditsMintedReal: number;
  creditsMintedDemo: number;
  consumedByLlm: number;
  consumedByAgentFee: number;
  consumedByMediagen: number;
  consumedByOther: number;
  providerCogs: number;
  poolAccrued: number;
  poolDistributed: number;
  payouts: number;
  planExpired: number;
}

interface Readout {
  days: number;
  rollupReady: boolean;
  daily: DailyRow[];
  liabilities: {
    outstandingPurchased: number;
    outstandingPlan: number;
    outstandingDemo: number;
    accruedRewards: number;
  } | null;
  pool: {
    configured: boolean;
    accrued: number | null;
    distributed: number | null;
    balance: number | null;
  };
  tiles: {
    realizedInferenceTake: number | null;
    realizedFeeRate: number | null;
    targets: { inferenceTake: number; feeRate: number; poolShare: number };
  };
}

function usd(nanocredits: number): string {
  return `$${(nanocredits / NANOCREDITS_PER_USD).toFixed(2)}`;
}

function pct(fraction: number): string {
  return `${(fraction * 100).toFixed(1)}%`;
}

function dayLabel(day: string): string {
  // finance_daily days arrive as ISO dates; show them as-is, trimmed of time.
  return day.slice(0, 10);
}

const WINDOWS = [7, 30, 90] as const;

interface OpsReadout {
  available: boolean;
  reason?: string;
  settleFailuresPerMin?: number;
  unbilledPerMin?: number;
  tokenErrorsPerMin?: number;
  revenueNanoPerHour?: number;
  watchdogAgeSeconds?: number;
  unpricedModelCalls24h?: number;
  ledgerDrift?: Array<{ check: string; value: number }>;
}

export function EconomicsTab() {
  const [data, setData] = useState<Readout | null>(null);
  const [ops, setOps] = useState<OpsReadout | null>(null);
  const [days, setDays] = useState<number>(30);
  const [forbidden, setForbidden] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    // Operational health rides its own fetch: a metrics outage must never
    // block the ledger readout, and vice versa.
    let cancelled = false;
    void (async () => {
      try {
        const r = await fetch('/api/admin/ops', { credentials: 'same-origin' });
        if (!r.ok) return;
        const body = (await r.json()) as OpsReadout;
        if (!cancelled) setOps(body);
      } catch {
        // The card renders its not-configured state.
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const load = useCallback(async (windowDays: number) => {
    setLoading(true);
    setError(null);
    try {
      const r = await fetch(`/api/admin/economics?days=${windowDays}`, {
        credentials: 'same-origin',
      });
      if (r.status === 403) {
        setForbidden(true);
        return;
      }
      if (!r.ok) throw new Error(`Request failed (${r.status})`);
      setData((await r.json()) as Readout);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load(days);
  }, [load, days]);

  const windowTotals = useMemo(() => {
    const zero = { cashIn: 0, fees: 0, poolAccrued: 0, poolDistributed: 0 };
    if (!data) return zero;
    return data.daily.reduce(
      (acc, row) => ({
        cashIn: acc.cashIn + row.cashInGross,
        fees: acc.fees + row.stripeFees,
        poolAccrued: acc.poolAccrued + row.poolAccrued,
        poolDistributed: acc.poolDistributed + row.poolDistributed,
      }),
      zero,
    );
  }, [data]);

  if (forbidden) {
    return (
      <div className="mx-auto max-w-2xl py-16 text-center">
        <ShieldAlert className="mx-auto mb-3 h-8 w-8 text-muted-foreground" />
        <h1 className="text-lg font-semibold">Admin access required</h1>
        <p className="mt-1 text-sm text-muted-foreground">
          You need an admin account to view economics.
        </p>
      </div>
    );
  }

  const tiles = data
    ? [
        {
          icon: Banknote,
          label: `Cash in, last ${data.days} days`,
          value: usd(windowTotals.cashIn),
          target: `Fees ${usd(windowTotals.fees)}`,
        },
        {
          icon: Gauge,
          label: 'AI margin',
          value:
            data.tiles.realizedInferenceTake == null
              ? 'No usage yet'
              : pct(data.tiles.realizedInferenceTake),
          target: `Target ${pct(data.tiles.targets.inferenceTake)}`,
        },
        {
          icon: Percent,
          label: 'Payment fee rate',
          value:
            data.tiles.realizedFeeRate == null ? 'No cash in yet' : pct(data.tiles.realizedFeeRate),
          target: `Target ${pct(data.tiles.targets.feeRate)}`,
        },
        {
          icon: Coins,
          label: 'Creator pool balance',
          value: data.pool.configured ? usd(data.pool.balance ?? 0) : 'Not set up',
          target: `Pool share target ${pct(data.tiles.targets.poolShare)} of net`,
        },
      ]
    : [];

  return (
    <div className="mx-auto max-w-5xl space-y-6 pb-12">
      <div className="flex items-center gap-2">
        <Landmark className="h-5 w-5" />
        <h1 className="text-xl font-semibold">Economics</h1>
      </div>
      <p className="text-sm text-muted-foreground">
        Daily money movement from the ledger: cash in, payment fees, credits consumed by type,
        provider costs, the creator pool, and what the platform owes. Figures update after the
        nightly settlement run.
      </p>

      {error ? (
        <div className="rounded-md border border-destructive/40 bg-destructive/5 px-3 py-2 text-sm text-destructive">
          {error}
        </div>
      ) : null}

      {loading && !data ? (
        <div className="py-12 text-center text-sm text-muted-foreground">Loading…</div>
      ) : null}

      {data ? (
        <>
          {!data.rollupReady ? (
            <div className="rounded-md border border-border bg-muted/40 px-3 py-2 text-sm">
              The daily rollup is not enabled on this environment yet, so the table below is
              empty. Liabilities and the pool position still read live where available.
            </div>
          ) : null}

          <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
            {tiles.map((t) => (
              <div key={t.label} className="rounded-lg border border-border p-3">
                <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
                  <t.icon className="h-3.5 w-3.5" />
                  {t.label}
                </div>
                <div className="mt-1 text-lg font-semibold">{t.value}</div>
                <div className="mt-0.5 text-xs text-muted-foreground">{t.target}</div>
              </div>
            ))}
          </div>

          <div className="flex flex-wrap items-center gap-3">
            <div className="flex items-center gap-1">
              {WINDOWS.map((w) => (
                <button
                  key={w}
                  type="button"
                  onClick={() => setDays(w)}
                  className={`rounded-md border px-2.5 py-1 text-sm ${
                    days === w
                      ? 'border-primary bg-primary text-primary-foreground'
                      : 'border-border hover:bg-muted'
                  }`}
                >
                  {w} days
                </button>
              ))}
            </div>
            <button
              type="button"
              onClick={() => void load(days)}
              disabled={loading}
              className="inline-flex items-center gap-1.5 rounded-md border border-border px-3 py-1.5 text-sm hover:bg-muted disabled:opacity-50"
            >
              <RefreshCw className={`h-3.5 w-3.5 ${loading ? 'animate-spin' : ''}`} />
              Refresh
            </button>
          </div>

          <div className="overflow-x-auto rounded-lg border border-border">
            <table className="w-full text-sm">
              <thead className="bg-muted/40 text-left text-xs text-muted-foreground">
                <tr>
                  <th className="px-3 py-2 font-medium">Day</th>
                  <th className="px-3 py-2 font-medium">Cash in</th>
                  <th className="px-3 py-2 font-medium">Fees</th>
                  <th className="px-3 py-2 font-medium">AI</th>
                  <th className="px-3 py-2 font-medium">Agents</th>
                  <th className="px-3 py-2 font-medium">Media</th>
                  <th className="px-3 py-2 font-medium">Other</th>
                  <th className="px-3 py-2 font-medium">Provider cost</th>
                  <th className="px-3 py-2 font-medium">Pool in</th>
                  <th className="px-3 py-2 font-medium">Pool out</th>
                </tr>
              </thead>
              <tbody>
                {data.daily.length === 0 ? (
                  <tr>
                    <td colSpan={10} className="px-3 py-6 text-center text-muted-foreground">
                      No money movement recorded in this window.
                    </td>
                  </tr>
                ) : (
                  data.daily.map((row) => (
                    <tr key={row.day} className="border-t border-border">
                      <td className="px-3 py-2 font-mono text-xs">{dayLabel(row.day)}</td>
                      <td className="px-3 py-2">{usd(row.cashInGross)}</td>
                      <td className="px-3 py-2">{usd(row.stripeFees)}</td>
                      <td className="px-3 py-2">{usd(row.consumedByLlm)}</td>
                      <td className="px-3 py-2">{usd(row.consumedByAgentFee)}</td>
                      <td className="px-3 py-2">{usd(row.consumedByMediagen)}</td>
                      <td className="px-3 py-2">{usd(row.consumedByOther)}</td>
                      <td className="px-3 py-2">{usd(row.providerCogs)}</td>
                      <td className="px-3 py-2">{usd(row.poolAccrued)}</td>
                      <td className="px-3 py-2">{usd(row.poolDistributed)}</td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>

          <div className="grid gap-3 md:grid-cols-2">
            <div className="rounded-lg border border-border p-4">
              <div className="flex items-center gap-1.5 text-sm font-semibold">
                <Scale className="h-4 w-4" />
                Outstanding balances
              </div>
              <p className="mt-1 text-xs text-muted-foreground">
                What users hold right now: credit balances the platform owes as service, and
                creator rewards it owes as cash.
              </p>
              {data.liabilities ? (
                <dl className="mt-3 space-y-1.5 text-sm">
                  <div className="flex items-center justify-between">
                    <dt className="text-muted-foreground">Purchased credits</dt>
                    <dd className="font-medium">{usd(data.liabilities.outstandingPurchased)}</dd>
                  </div>
                  <div className="flex items-center justify-between">
                    <dt className="text-muted-foreground">Plan credits</dt>
                    <dd className="font-medium">{usd(data.liabilities.outstandingPlan)}</dd>
                  </div>
                  <div className="flex items-center justify-between">
                    <dt className="text-muted-foreground">Demo credits</dt>
                    <dd className="font-medium">{usd(data.liabilities.outstandingDemo)}</dd>
                  </div>
                  <div className="flex items-center justify-between">
                    <dt className="text-muted-foreground">Creator rewards</dt>
                    <dd className="font-medium">{usd(data.liabilities.accruedRewards)}</dd>
                  </div>
                </dl>
              ) : (
                <p className="mt-3 text-sm text-muted-foreground">Not available yet.</p>
              )}
            </div>

            <div className="rounded-lg border border-border p-4">
              <div className="flex items-center gap-1.5 text-sm font-semibold">
                <Coins className="h-4 w-4" />
                Creator pool
              </div>
              <p className="mt-1 text-xs text-muted-foreground">
                Accrued from usage and expiries, paid out by the settlement schedule. The balance
                carries forward until distributed.
              </p>
              {data.pool.configured ? (
                <dl className="mt-3 space-y-1.5 text-sm">
                  <div className="flex items-center justify-between">
                    <dt className="text-muted-foreground">Accrued</dt>
                    <dd className="font-medium">{usd(data.pool.accrued ?? 0)}</dd>
                  </div>
                  <div className="flex items-center justify-between">
                    <dt className="text-muted-foreground">Distributed</dt>
                    <dd className="font-medium">{usd(data.pool.distributed ?? 0)}</dd>
                  </div>
                  <div className="flex items-center justify-between">
                    <dt className="text-muted-foreground">Balance</dt>
                    <dd className="font-medium">{usd(data.pool.balance ?? 0)}</dd>
                  </div>
                </dl>
              ) : (
                <p className="mt-3 text-sm text-muted-foreground">
                  The pool account is not set up on this environment, so nothing accrues here yet.
                </p>
              )}
            </div>
          </div>

          <div className="rounded-lg border border-border p-4">
            <div className="flex items-center gap-1.5 text-sm font-semibold">
              <Activity className="h-4 w-4" />
              Operational health
            </div>
            <p className="mt-1 text-xs text-muted-foreground">
              Live rates from monitoring, aggregated across all pods. Alerts fire on these
              independently of this page.
            </p>
            {ops?.available ? (
              <dl className="mt-3 grid grid-cols-2 gap-x-6 gap-y-1.5 text-sm sm:grid-cols-3">
                <div className="flex items-center justify-between">
                  <dt className="text-muted-foreground">Revenue rate</dt>
                  <dd className="font-medium">{usd(ops.revenueNanoPerHour ?? 0)}/h</dd>
                </div>
                <div className="flex items-center justify-between">
                  <dt className="text-muted-foreground">Settle failures</dt>
                  <dd className={`font-medium ${(ops.settleFailuresPerMin ?? 0) > 0 ? 'text-destructive' : ''}`}>
                    {(ops.settleFailuresPerMin ?? 0).toFixed(2)}/min
                  </dd>
                </div>
                <div className="flex items-center justify-between">
                  <dt className="text-muted-foreground">Unbilled work</dt>
                  <dd className={`font-medium ${(ops.unbilledPerMin ?? 0) > 0 ? 'text-destructive' : ''}`}>
                    {(ops.unbilledPerMin ?? 0).toFixed(2)}/min
                  </dd>
                </div>
                <div className="flex items-center justify-between">
                  <dt className="text-muted-foreground">Watchdog ran</dt>
                  <dd className="font-medium">
                    {ops.watchdogAgeSeconds != null ? `${Math.round(ops.watchdogAgeSeconds / 60)} min ago` : 'unknown'}
                  </dd>
                </div>
                <div className="flex items-center justify-between">
                  <dt className="text-muted-foreground">Unpriced AI calls, 24h</dt>
                  <dd className={`font-medium ${(ops.unpricedModelCalls24h ?? 0) > 0 ? 'text-destructive' : ''}`}>
                    {Math.round(ops.unpricedModelCalls24h ?? 0)}
                  </dd>
                </div>
                <div className="flex items-center justify-between">
                  <dt className="text-muted-foreground">Ledger drift</dt>
                  <dd className={`font-medium ${(ops.ledgerDrift ?? []).some((d) => d.value > 0) ? 'text-destructive' : ''}`}>
                    {(ops.ledgerDrift ?? []).some((d) => d.value > 0)
                      ? (ops.ledgerDrift ?? []).filter((d) => d.value > 0).map((d) => d.check).join(', ')
                      : 'none'}
                  </dd>
                </div>
              </dl>
            ) : (
              <p className="mt-3 text-sm text-muted-foreground">
                {ops?.reason === 'PROMETHEUS_URL not configured'
                  ? 'Metrics source is not configured on this environment.'
                  : 'Metrics are unavailable right now. Alerting is unaffected.'}
              </p>
            )}
          </div>
        </>
      ) : null}
    </div>
  );
}
